import { formatValueAsString } from "./helpers";

// Utility function to convert data to CSV and trigger download
const formatOptions = { maximumFractionDigits: 2, minimumFractionDigits: 1 };

export function downloadDataAsCsv<T extends Record<string, any>>(data: T[], filename: string): void {
    if (!data || data.length === 0) {
        console.warn('No data to download');
        return;
    }

    // Get all unique headers from all items in the data array
    const headers = Array.from(
        new Set(
            data.flatMap(item => Object.keys(item as Record<string, any>))
        )
    );

    // Convert camelCase headers to meaningful display names
    const displayHeaders = headers.map(header => convertCamelCaseToDisplayName(header));

    // Create CSV content
    const csvContent = [
        // Headers row with meaningful names
        displayHeaders.join(','),
        // Data rows
        ...data.map(item => headers.map(header => {
            const value = item[header as keyof T];            // Handle null/undefined values
            if (value === null || value === undefined) {
                return '';
            }

            // Handle date values in various formats
            const formattedDate = formatDateValue(value);
            if (formattedDate !== null) {
                return formattedDate;
            }

            // Handle numeric values to prevent precision issues and single quotes
            if (typeof value === 'number') {
                // Check if it's an integer
                if (Number.isInteger(value)) {
                    return value.toString();
                }

                // For floating point numbers, format as string with specified options
                return `"${formatValueAsString(value, formatOptions)}"`;


            }
            // Handle string values that might contain commas
            if (typeof value === 'string' && value.includes(',')) {
                return `"${value}"`;
            }
            // Convert other values to string safely
            return String(value);
        }).join(',')
        )
    ].join('\n');

    // Create a Blob with the CSV content
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    // Create a download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);

    // Append to body, click and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

// Interface for chart data with metadata
export interface Dataset<T extends Record<string, any>> {
    name: string;
    data?: T[] | null; // Data can be undefined if no data is available
    description?: string;
}

// Function to download multiple chart datasets as a single CSV with sections
export function downloadMultipleDataAsCsv<T extends Record<string, any>>(
    datasets: Dataset<T>[],
    filename: string
): void {
    if (!datasets || datasets.length === 0) {
        console.warn('No datasets to download');
        return;
    }

    // Filter out empty datasets
    const validDatasets = datasets.filter(dataset => dataset.data && dataset.data.length > 0);

    if (validDatasets.length === 0) {
        console.warn('No valid data to download');
        return;
    }

    const csvSections: string[] = [];

    validDatasets.forEach((dataset, index) => {
        // Add section header
        csvSections.push(`\n=== ${dataset.name.toUpperCase()} ===`);

        // Add description if provided
        if (dataset.description) {
            csvSections.push(`Description: ${dataset.description}`);
        }

        // Add empty line for readability
        csvSections.push('');

        // Get headers from the first item of this dataset
        // const headers = Object.keys(dataset.data![0] as Record<string, any>);
        // Get all unique headers from all items in the data array
        const headers = Array.from(
            new Set(
                dataset.data!.flatMap(item => Object.keys(item as Record<string, any>))
            )
        );
        const displayHeaders = headers.map(header => convertCamelCaseToDisplayName(header));

        // Add headers
        csvSections.push(displayHeaders.join(','));

        // Add data rows for this dataset
        dataset.data!.forEach(item => {
            const row = headers.map(header => {
                const value = item[header as keyof T];

                // Handle null/undefined values
                if (value === null || value === undefined) {
                    return '';
                }

                // Handle date values
                const formattedDate = formatDateValue(value);
                if (formattedDate !== null) {
                    return formattedDate;
                }

                // Handle numeric values
                if (typeof value === 'number') {
                    if (Number.isInteger(value)) {
                        return value.toString();
                    }
                    return `"${formatValueAsString(value, formatOptions)}"`;
                }

                // Handle string values that might contain commas
                if (typeof value === 'string' && value.includes(',')) {
                    return `"${value}"`;
                }

                return String(value);
            });

            csvSections.push(row.join(','));
        });

        // Add spacing between datasets (except for the last one)
        if (index < validDatasets.length - 1) {
            csvSections.push('');
            csvSections.push('');
        }
    });

    // Create final CSV content
    const csvContent = csvSections.join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

// Utility function to convert camelCase to meaningful header names
function convertCamelCaseToDisplayName(camelCaseString: string): string {
    // Handle common abbreviations and acronyms
    const abbreviationMap: Record<string, string> = {
        'aer': 'AER',
        'cii': 'CII',
        'imo': 'IMO',
        'dwt': 'DWT',
        'nm': 'NM',
        'co2': 'CO₂',
        'eca': 'ECA',
        'wtw': 'WtW',
        'ttw': 'TtW',
        'cg': 'CG',
        'dist': 'Distance',
        'api': 'API',
        'url': 'URL',
        'id': 'ID',
        'ui': 'UI',
        'ux': 'UX',
        'gps': 'GPS',
        'lat': 'Latitude',
        'lng': 'Longitude',
        'lon': 'Longitude',
        'kpi': 'KPI',
        'kpis': 'KPIs',
        'ghg': 'GHG',
        'sox': 'SOx',
        'nox': 'NOx',
        'pm': 'PM',
        'hfo': 'HFO',
        'mgo': 'MGO'
    };

    // Handle special compound cases first
    const specialCases: Record<string, string> = {
        'signalDateTime': 'Signal Date Time',
        'dateTime': 'Date Time',
        'startTime': 'Start Time',
        'endTime': 'End Time',
        'createTime': 'Create Time',
        'updateTime': 'Update Time'
    };

    // Check for special compound cases first
    if (specialCases[camelCaseString]) {
        return specialCases[camelCaseString];
    }

    // Split camelCase into words
    const words = camelCaseString
        .replace(/([a-z])([A-Z])/g, '$1 $2') // Insert space before uppercase letters
        .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Handle consecutive uppercase letters
        .split(' ')
        .map(word => word.toLowerCase());

    // Convert each word, checking for abbreviations first
    const convertedWords = words.map(word => {
        // Check if the word is a known abbreviation
        if (abbreviationMap[word]) {
            return abbreviationMap[word];
        }

        // Handle special cases for common terms
        if (word === 'value') {
            return 'Value';
        }
        if (word === 'score') {
            return 'Score';
        }
        if (word === 'signal') {
            return 'Signal';
        }
        if (word === 'rolling') {
            return 'Rolling';
        }
        if (word === 'number') {
            return 'Number';
        }
        if (word === 'time') {
            return 'Time';
        }
        if (word === 'date') {
            return 'Date';
        }
        if (word === 'emissions') {
            return 'Emissions';
        }
        if (word === 'consumption') {
            return 'Consumption';
        }
        if (word === 'efficiency') {
            return 'Efficiency';
        }

        // Handle single letter values (A, B, C, D, E for emission categories)
        if (word.length === 1 && /[a-zA-Z]/.test(word)) {
            return word.toUpperCase();
        }

        // Capitalize first letter for regular words
        return word.charAt(0).toUpperCase() + word.slice(1);
    });

    return convertedWords.join(' ');
}

// Utility function to check if a value is a valid date and format it
function formatDateValue(value: any): string | null {
    // Handle Date objects
    if (Object.prototype.toString.call(value) === '[object Date]' && !isNaN((value as Date).getTime())) {
        return `"${(value as Date).toISOString().replace('T', ' ').slice(0, 19)}"`;
    }

    // Handle string values that might be dates
    if (typeof value === 'string') {
        // Check for ISO date strings (2024-03-12T03:00:00)
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
            return `"${new Date(value).toISOString().replace('T', ' ').slice(0, 19)}"`;
        }
        // Check for full date strings (Tue Mar 12 2024 03:00:00 GMT+0000 (Greenwich Mean Time))
        if (/^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{1,2}\s+\d{4}\s+\d{2}:\d{2}:\d{2}/.test(value)) {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
                return `"${date.toISOString().replace('T', ' ').slice(0, 19)}"`;
            }
        }
        // Check for other common date patterns
        if (/^\d{4}-\d{2}-\d{2}$/.test(value) || /^\d{2}\/\d{2}\/\d{4}$/.test(value) || /^\d{1,2}-\d{1,2}-\d{4}$/.test(value)) {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
                return `"${date.toISOString().slice(0, 10)}"`;
            }
        }
    }

    return null;
}

/*
Supported date formats for CSV export:
- ISO date strings: "2024-03-12T03:00:00"
- Full date strings: "Tue Mar 12 2024 03:00:00 GMT+0000 (Greenwich Mean Time)"
- Simple date formats: "2024-03-12", "03/12/2024", "12-3-2024"
- Date objects: new Date()
*/

/*
Example transformations for IRollingAerValues:
- signalDateTime → "Signal Date Time"
- rollingAerCgDist → "Rolling AER CG Distance"
- rollingAerCgDistWtw → "Rolling AER CG Distance WTW"
- rollingCiiScore → "Rolling CII Score"
- aValue → "A Value"
- bValue → "B Value"
- cValue → "C Value"
- dValue → "D Value"
- eValue → "E Value"
*/
